import React, { useEffect } from "react";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Prozess } from "@/components/site/Prozess";
import { CaseCards } from "@/components/site/CaseCards";
import { StelleBesetzenForm } from "@/components/site/StelleBesetzenForm";
import { Ansprechpartner } from "@/components/site/Ansprechpartner";
import { Faq } from "@/components/site/Faq";
import { FlowLines } from "@/components/site/FlowLines";
import { AccentH2, Container, Label, Reveal, Section } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";
import { ICONS } from "@/pages/PersonalHub";
import personal from "@/data/personal.json";
import ratgeber from "@/data/ratgeber.json";
import lexikon from "@/data/lexikon.json";

const Hero = ({ slug, d }) => (
  <section data-testid="rolle-hero" className="has-lines relative overflow-hidden bg-[#1e2532] text-white on-dark">
    <FlowLines />
    <div className="hero-pad mx-auto grid max-w-[1240px] grid-cols-1 items-center gap-9 px-6 md:px-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
      <div>
        <nav aria-label="Breadcrumb" data-testid="rolle-breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-[13px] text-white/50">
          <a href="/" className="hover:text-white">Startseite</a>
          <ChevronRight size={13} />
          <a href="/personalvermittlung/" className="hover:text-white">Personal</a>
          <ChevronRight size={13} />
          <span className="text-white/80">{d.breadcrumb}</span>
        </nav>
        <Label>Personalvermittlung</Label>
        <h1 className="mt-7 font-bold" data-testid="rolle-h1">
          {d.h1}
        </h1>
        <p className="mt-6 max-w-[620px] text-[16.5px] leading-[1.72] text-white/72">{d.subline}</p>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <a
            href="#formular"
            data-testid="rolle-primary-cta"
            className="btn-solid inline-flex items-center px-7 py-[15px] text-[13.5px] font-bold uppercase tracking-[0.06em]"
          >
            <span>Stelle besetzen</span>
          </a>
          <a href="#ablauf" data-testid="rolle-secondary-cta" className="inline-flex items-center gap-2.5 text-[15px] font-semibold text-white/90">
            <span className="link-underline">Ablauf ansehen</span>
            <ArrowRight size={17} />
          </a>
        </div>
      </div>
      <div data-testid="rolle-facts" className="rounded-[18px] border border-white/12 bg-white/[0.04] p-7 md:p-8">
        <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-[#3ecf9b]">Auf einen Blick</p>
        <dl className="mt-6 space-y-6">
          {d.facts.map((f) => (
            <div key={f.label} className="border-b border-white/10 pb-5 last:border-0 last:pb-0">
              <dt className="text-[13.5px] text-white/55">{f.label}</dt>
              <dd className="mt-1.5 font-display text-[24px] font-bold leading-[1.2] text-white">{f.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  </section>
);

const Engpass = ({ d }) => (
  <Section testId="rolle-engpass" className="has-lines overflow-hidden">
    <FlowLines color="#0F9D6E" opacity={[0.035, 0.028]} />
    <Container>
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
        <Reveal>
          <Label>Marktlage</Label>
          <AccentH2 className="mt-6 font-bold text-[#1e2532]">{d.h2}</AccentH2>
        </Reveal>
        <Reveal>
          {d.paragraphs.map((p) => (
            <p key={p.slice(0, 40)} className="mt-5 text-[16.5px] leading-[1.85] text-[#3c4356] first:mt-0">
              {p}
            </p>
          ))}
          <blockquote
            data-testid="rolle-quote"
            className="mt-9 border-l-[3px] border-[#0f9d6e] bg-[#f2f8f5] px-7 py-6 font-display text-[19px] font-medium leading-[1.5] text-[#1e2532] md:text-[21px]"
          >
            „{d.quote}“
          </blockquote>
        </Reveal>
      </div>
    </Container>
  </Section>
);

const Positionen = ({ d }) => (
  <Section testId="rolle-positionen" className="has-lines overflow-hidden bg-[#f5f6f4]">
    <FlowLines color="#0F9D6E" opacity={[0.055, 0.045]} />
    <Container>
      <Reveal>
        <Label>Positionen</Label>
        <AccentH2 className="mt-6 max-w-[760px] font-bold text-[#1e2532]">{d.h2}</AccentH2>
      </Reveal>
      <div className="mt-10 flex flex-wrap gap-2.5">
        {d.items.map((it) => (
          <span
            key={it}
            className="rounded-full border border-[#d4d8d5] bg-white px-4 py-2.5 text-[14.5px] font-medium text-[#3c4356]"
          >
            {it}
          </span>
        ))}
      </div>
    </Container>
  </Section>
);

const Suche = ({ d }) => (
  <Section testId="rolle-suche" className="has-lines overflow-hidden">
    <FlowLines color="#0F9D6E" opacity={[0.035, 0.028]} />
    <Container>
      <Reveal>
        <Label>Vorgehen</Label>
        <AccentH2 className="mt-6 max-w-[800px] font-bold text-[#1e2532]">{d.h2}</AccentH2>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-7">
        {d.cards.map((cd, i) => (
          <div key={cd.h3} data-testid={`rolle-suche-${i}`} className="card-hover h-full rounded-[16px] border border-[#dfe2e0] bg-white p-7 md:p-8">
            <span className="font-display text-[13px] font-bold tracking-[0.14em] text-[#0f9d6e]">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-4 text-[18px] font-semibold leading-[1.35] text-[#1e2532]">{cd.h3}</h3>
            <span className="accent-rule mt-4" aria-hidden="true" />
            <p className="mt-4 text-[15.5px] leading-[1.74] text-[#565f70]">{cd.text}</p>
          </div>
        ))}
      </div>
    </Container>
  </Section>
);

const Profil = ({ d }) => (
  <Section testId="rolle-profil" className="has-lines overflow-hidden bg-[#1e2532] text-white on-dark">
    <FlowLines />
    <Container>
      <Reveal>
        <Label>Anforderungsprofil</Label>
        <AccentH2 className="mt-6 max-w-[780px] font-bold text-white">{d.h2}</AccentH2>
      </Reveal>
      <ul className="mt-10 grid grid-cols-1 gap-x-12 gap-y-4 md:grid-cols-2">
        {d.items.map((it) => (
          <li key={it} className="flex gap-3 border-b border-white/10 pb-4 text-[16px] leading-[1.6] text-white/85">
            <span className="mt-[3px] flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-full bg-[#0f9d6e]">
              <Check size={11} strokeWidth={3} className="text-white" />
            </span>
            {it}
          </li>
        ))}
      </ul>
    </Container>
  </Section>
);

const Markt = ({ d }) => (
  <Section testId="rolle-markt" className="has-lines overflow-hidden">
    <FlowLines color="#0F9D6E" opacity={[0.035, 0.028]} />
    <Container>
      <Reveal>
        <Label>Marktinfo</Label>
        <AccentH2 className="mt-6 max-w-[760px] font-bold text-[#1e2532]">{d.h2}</AccentH2>
      </Reveal>
      <div className="mt-10 overflow-hidden rounded-[16px] border border-[#dfe2e0]">
        <table className="w-full border-collapse text-left">
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={r.label} className={i % 2 ? "bg-[#f8f9f8]" : "bg-white"}>
                <th scope="row" className="w-[42%] border-b border-[#e8eae8] px-5 py-4 text-[14.5px] font-semibold text-[#1e2532] md:px-7 md:text-[15.5px]">
                  {r.label}
                </th>
                <td className="border-b border-[#e8eae8] px-5 py-4 text-[14.5px] leading-[1.6] text-[#565f70] md:px-7 md:text-[15.5px]">
                  {r.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  </Section>
);

const Weiterlesen = ({ d, slug }) => {
  const nachbarn = d.nachbarn.map((s) => ({ slug: s, ...personal.rollen[s] }));
  return (
    <Section testId="rolle-weiterlesen" className="has-lines overflow-hidden bg-[#f5f6f4]">
      <FlowLines color="#0F9D6E" opacity={[0.055, 0.045]} />
      <Container>
        <Reveal>
          <Label>Weiterlesen</Label>
          <AccentH2 className="mt-6 max-w-[760px] font-bold text-[#1e2532]">Passende Ratgeber und Begriffe</AccentH2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-7">
          {d.ratgeber
            .filter((s) => ratgeber[s])
            .map((s) => (
              <a
                key={s}
                href={`/ratgeber/${s}/`}
                data-testid={`rolle-ratgeber-${s}`}
                className="card-hover flex h-full flex-col overflow-hidden rounded-[16px] border border-[#dfe2e0] bg-white"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={ratgeber[s].image} alt={ratgeber[s].title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#0b7d57]">{ratgeber[s].category}</span>
                  <h3 className="mt-3 text-[17px] font-semibold leading-[1.35] text-[#1e2532]">{ratgeber[s].title}</h3>
                  <span className="mt-auto inline-flex items-center gap-2 pt-5 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
                    Artikel lesen <ArrowRight size={15} />
                  </span>
                </div>
              </a>
            ))}
        </div>

        <div className="mt-12 rounded-[16px] border border-[#dfe2e0] bg-white p-7 md:p-8">
          <h3 className="text-[17px] font-semibold text-[#1e2532]">Begriffe aus dem Lexikon</h3>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {d.lexikon
              .filter((s) => lexikon[s])
              .map((s) => (
                <a
                  key={s}
                  href={`/lexikon/${s}/`}
                  data-testid={`rolle-lexikon-${s}`}
                  className="rounded-full border border-[#d4d8d5] bg-[#f8f9f8] px-4 py-2 text-[14px] font-medium text-[#3c4356] hover:border-[#0f9d6e] hover:text-[#0b7d57]"
                  style={{ transition: "border-color .3s ease, color .3s ease" }}
                >
                  {lexikon[s].term}
                </a>
              ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
          {nachbarn.map((n) => (
            <a
              key={n.slug}
              href={`/personalvermittlung/${n.slug}/`}
              data-testid={`rolle-nachbar-${n.slug}`}
              className="card-hover rounded-[16px] border border-[#dfe2e0] bg-white p-6"
            >
              <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#0b7d57]">Weitere Rolle</p>
              <p className="mt-3 text-[17px] font-semibold text-[#1e2532]">{n.navLabel}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
                Zur Rolle <ArrowRight size={15} />
              </span>
            </a>
          ))}
          <a
            href={d.jobsHref}
            data-testid="rolle-jobs-link"
            className="card-hover rounded-[16px] border border-[#0f9d6e]/30 bg-[#f2f8f5] p-6"
          >
            <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#0b7d57]">Für Bewerber</p>
            <p className="mt-3 text-[17px] font-semibold text-[#1e2532]">Offene Stellen ansehen</p>
            <span className="mt-4 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
              Zu den Jobs <ArrowRight size={15} />
            </span>
          </a>
        </div>
      </Container>
    </Section>
  );
};

export default function RollePage({ slug }) {
  const d = personal.rollen[slug];
  useEffect(() => {
    document.title = d.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", d.metaDescription);
    window.scrollTo(0, 0);
  }, [d]);

  const Icon = ICONS[d.icon];
  return (
    <>
      <Header />
      <main data-testid={`rolle-main-${slug}`}>
        <Hero slug={slug} d={d} />
        <Engpass d={d.engpass} />
        <Positionen d={d.positionen} />
        <Suche d={d.suche} />
        <Profil d={d.profil} />
        <Markt d={d.markt} />
        <CaseCards cases={[personal.hub.cases[d.caseIndex]]} h2="Ergebnis aus der Praxis" />
        <Prozess />
        <Faq items={d.faq.items} h2={d.faq.h2} label="FAQ" />
        <Weiterlesen d={d} slug={slug} />
        <StelleBesetzenForm defaultPosition={d.navLabel} />
        <Ansprechpartner />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
